import { z } from "zod";
import type { IntlShape } from "react-intl";
import { levelFromPercent } from "@/lib/skill-levels";
import type {
  RankThresholds,
  RankingAiCriterionInsight,
  RankingAiInsights,
  RankingCriterionInsight,
  RankingDataQualityInsights,
  RankingInsight,
  RankingInsights,
  RankingPoolInsights,
  RankingRecommendation,
} from "./match-result-derive";
import type { Classification } from "./match-result-codec";

// THE COMPANY RANKING, DERIVED FROM WHAT GENESIS STORED — and nothing more.
//
// genesis stores ATOMS per criterion (@adr SPARK-0030-match-result-scoring-atom-derived@1 §2, mirrored on the company side by
// `CompanyMatchResultItem`): Muss/Kann, the outcome, the degree of fulfilment, the actual value the
// run found on the company, and the AI's reason where one was needed. It stores NO percentage, NO
// A/B/C band and no total. This module is where
// those come from, which is why they are recomputed on every render and why moving the threshold
// slider changes the bands without touching the server: "compute once, threshold many", the same
// arrangement the person ranking has.
//
// THE ONE RULE THAT MATTERS MORE THAN THE ARITHMETIC: a criterion that could not be assessed reads
// "cannot tell" and NEVER "not met". genesis says the same thing in three places — CANNOT TELL is
// its own classification, `fulfillment` is null (not 0) exactly then, and `CompanyRanking` refuses
// to let an unassessable Muss sink a company behind one that is provably wrong. A company with no
// industry on file must sort between "right industry" and "wrong industry", not with the wrong
// ones, and it must not be told it failed a check nobody could run.
//
// WHAT IS NOT HERE: powers. A company has no power profile — no self-assessment, no 30 elements,
// nothing to score — so there is no power axis to fold in, no power-fit sort and no cluster
// diagram. That is not "not yet": genesis's company arm never reaches the solver or the power
// model at all, so a powers axis on this surface would be an assertion about data that does not
// exist.

/**
 * The four outcomes, exactly the person side's vocabulary — genesis reuses its own enum too.
 *
 * <b>`PARTIAL` STAYS, even though no NEW company result can contain it.</b> Concept v17 made every
 * company criterion binary (company expertise lost its levels, and the expertise arm was the only
 * producer of a graded evaluation), so genesis emits SATISFIED / VIOLATED / UNKNOWN from here on.
 * Results SAVED BEFORE that still carry PARTIAL atoms, and a saved ranking is history that must keep
 * opening — dropping the member from this enum would make those results fail to parse rather than
 * render. The same reasoning keeps the "teilweise erfüllt" branch in `CompanyRankingPanel`.
 */
const ClassificationSchema = z.enum(["SATISFIED", "PARTIAL", "VIOLATED", "UNKNOWN"]);

const CriterionItemSchema = z
  .object({
    // The evaluated company criterion, `__typename` and all. Kept as unknown: this module orders
    // and counts, it does not label — labelling needs the industry catalogue and `intl`, which
    // belong to the screen.
    condition: z.unknown().nullish(),
    isMust: z.boolean().nullish(),
    // catch(null) for the reason match-result-codec gives: a fifth outcome from a newer genesis
    // must cost this one field, not the whole ranking. A null classification is read as "cannot
    // tell", which is the answer that cannot misrepresent it.
    classification: ClassificationSchema.nullish().catch(null),
    // 0..1, and null EXACTLY when the criterion could not be assessed. The difference between
    // "0 = disproved" and "null = nothing to judge" is the whole point of the field.
    fulfillment: z.number().nullish(),
    // THE VALUE THE RUN FOUND, as genesis recorded it at evaluation time: the industry as an id, a
    // head count as a number, an expertise as the company's own matching label plus its 0..100
    // degree. A union of string and number for the same reason the person codec has one — a
    // numeric actual is a number in JSON and must not cost the field.
    //
    // Three states, not two, and the screen needs all three: a string is a value, an explicit null
    // is "recorded, and there is none", and an ABSENT key is "this ranking was stored before actual
    // values existed". genesis writes the key always (JsonInclude.ALWAYS) precisely so the last one
    // stays distinguishable — see parseCompanyResult.
    actual: z.union([z.string(), z.number()]).nullish(),
    aiReason: z.string().nullish(),
  })
  .passthrough();

const CompanyResultBlobSchema = z
  .object({
    criteria: z.array(CriterionItemSchema).default([]),
  })
  .passthrough();

/** One criterion's outcome for one company, ready to render. */
export interface CompanyCriterionView {
  readonly condition: unknown;
  readonly isMust: boolean;
  readonly classification: Classification;
  /** null exactly when {@link assessable} is false. */
  readonly fulfillment: number | null;
  /**
   * The value the RUN found on the company for this criterion, as genesis recorded it — raw, so the
   * screen formats it per criterion kind. Null when there is none: the company does not maintain
   * the value, or, for an expertise criterion, nothing it lists matched the wording.
   *
   * Meaningless on its own — read {@link actualRecorded} first. Null means "no value" only when the
   * run recorded values at all; otherwise it means "we do not know", which is a different sentence.
   */
  readonly actual: string | null;
  /**
   * Whether this atom carried an `actual` key at all.
   *
   * False for a ranking stored before genesis recorded actual values. It has to be separate from
   * `actual === null`, because the two must not be shown alike: "the company lists nothing matching"
   * is a claim about the company, and making it about a row that simply never recorded one would be
   * a claim this build cannot support.
   */
  readonly actualRecorded: boolean;
  /** Why the AI decided this, when a hard check could not settle it. */
  readonly aiReason: string | null;
  /** False = "nicht bewertbar". Never rendered as unmet. */
  readonly assessable: boolean;
}

/**
 * Where a company stands, as a word rather than a number.
 *
 * `UNQUALIFIED` comes ONLY from a disproved Muss or a score under the C floor — never from missing
 * information. `REVIEW` is the honest middle: nothing is disproved, but something a decision would
 * rest on could not be assessed, so the company is neither in a band nor out of the running.
 */
export type CompanyBand = "A" | "B" | "C" | "REVIEW" | "UNQUALIFIED";

/** The Muss gate, with three outcomes rather than two (spark#175 / @adr SPARK-0030-match-result-scoring-atom-derived@1 §1). */
export type CompanyQualification = "QUALIFIED" | "REVIEW_REQUIRED" | "UNQUALIFIED";

export interface RankedCompany {
  /** The `match_result_company` row — this ranking's own identity for the row. */
  readonly entryId: string;
  readonly companyId: string;
  readonly name: string;
  /** 1-based and stored by genesis. THE ORDER IS THE SERVER'S; nothing here re-sorts. */
  readonly position: number;
  /**
   * The Muss gate's outcome — threshold-independent, so it is settled here. The A/B/C BAND is not
   * on this record on purpose: it depends on where the reader put the sliders, and a row carrying
   * one band while the screen draws another is the drift this split exists to prevent. Ask
   * {@link companyBand}.
   */
  readonly qualification: CompanyQualification;
  /**
   * Mean Kann fulfilment, 0..1 — or null when Kann criteria exist and none of them could be
   * assessed. Null is not 0: "we could not tell" must not read as "it scored nothing".
   */
  readonly score: number | null;
  readonly mustMet: number;
  readonly mustTotal: number;
  /** Kann criteria that could actually be assessed, and how many there are in total. */
  readonly optionalAssessed: number;
  readonly optionalTotal: number;
  /** Criteria nothing could assess — the count the row states in words. */
  readonly unassessable: number;
  readonly criteria: readonly CompanyCriterionView[];
  readonly inactive: boolean;
  /**
   * The company's values as they are NOW — the FALLBACK for a ranking stored before genesis
   * recorded actual values, and nothing more.
   *
   * genesis now carries the judged value per criterion (`CompanyCriterionView.actual`), which is
   * what a drill-down must print: it is the state at the time of the run, so a saved ranking keeps
   * showing what was judged rather than what the company says today. These two fields stay because
   * an OLD row has no such value, and dropping them would turn every stored ranking's evidence into
   * "keine Angabe". They are only ever read when `actualRecorded` is false — and when they are, the
   * panel says on screen that these are today's values.
   *
   * There is deliberately no third field for an expertise: that value never existed on this side.
   * Which of the company's labels matched a wording is a result of genesis's fold, not a column, so
   * an old row's expertise criterion has no actual and reads as unrecorded.
   */
  readonly industryId: string | null;
  readonly employeeCount: number | null;
}

/**
 * The A/B/C cut, shared with the person ranking rather than declared twice.
 *
 * <p>Until the spark!285 review (point 1) this was its own `CompanyRankThresholds` interface with the
 * same three members, and the company panel set them with number fields while the candidate panel
 * used a three-handle slider. One type and one control: the bands mean "strong / workable / weak" on
 * both surfaces, and two declarations were an invitation for the two to drift.
 */
export type CompanyRankThresholds = RankThresholds;
/**
 * Same numbers as the person ranking's defaults, and same reason to share them: the bands mean
 * "strong / workable / weak" on both surfaces, and two different defaults would make the same word
 * mean two things.
 */
export const DEFAULT_COMPANY_THRESHOLDS: CompanyRankThresholds = { a: 0.8, b: 0.5, c: 0 };

// The step the A/B/C handles move in lives in RankingThresholdSlider since the spark!285 review —
// one control, one step. `COMPANY_THRESHOLD_STEP_PCT` stood here and had no second reader.

/** One ranking row as it arrives from `MatchResult.companies`. */
export interface CompanyRankingRowInput {
  readonly entryId: string;
  readonly position: number;
  readonly result?: Record<string, unknown> | null;
  readonly company?: {
    readonly id: string;
    readonly name: string;
    readonly inactive: boolean;
    readonly industryId: string | null;
    readonly employeeCount: number | null;
  } | null;
}

/** The criteria atoms of one row, or an empty list when the blob carries none it can read. */
export function parseCompanyResult(blob: Record<string, unknown> | null | undefined): CompanyCriterionView[] {
  if (!blob) return [];
  const parsed = CompanyResultBlobSchema.safeParse(blob);
  if (!parsed.success) return [];
  return parsed.data.criteria.map((item) => {
    // Two independent signals for the same fact, and either one is enough. genesis sets
    // fulfillment null exactly when the outcome is UNKNOWN, so agreement is the normal case;
    // reading BOTH means a blob that lost one of them still cannot turn "cannot tell" into a
    // verdict.
    const classification: Classification = item.classification ?? "UNKNOWN";
    // Read once, so `assessable` and the value it certifies are the same fact rather than two
    // checks that could drift — and so the field below needs no fallback for a case `assessable`
    // has already excluded.
    const fulfillment = item.fulfillment ?? null;
    const assessable = classification !== "UNKNOWN" && fulfillment !== null;
    return {
      condition: item.condition ?? null,
      // null requirement is KANN, the reading genesis's own record pins.
      isMust: item.isMust === true,
      classification,
      fulfillment: assessable ? fulfillment : null,
      // `undefined` is the ABSENT key and nothing else: JSON cannot carry an explicit undefined, so
      // the distinction survives the parse without a second field in the blob.
      actualRecorded: item.actual !== undefined,
      actual: item.actual == null ? null : String(item.actual),
      aiReason: item.aiReason ?? null,
      assessable,
    };
  });
}

/**
 * The ranking as the screen draws it: server order kept, bands derived.
 *
 * Rows are returned IN THE ORDER GIVEN, sorted only by the stored `position`. The band is a reading
 * aid over that order and not a re-ordering of it — genesis's rank rule (no disproved Muss, then
 * Muss fulfilment, then Kann, then pool order) is lexicographic and persisted, so a UI that sorted
 * by its own single score would silently disagree with the position it prints next to every row.
 */
export function buildCompanyRanking(rows: readonly CompanyRankingRowInput[]): RankedCompany[] {
  return rows
    .filter((row): row is CompanyRankingRowInput & { company: NonNullable<CompanyRankingRowInput["company"]> } =>
      Boolean(row.company),
    )
    .map((row) => {
      const criteria = parseCompanyResult(row.result);
      let mustViolated = false;
      let mustUnknown = false;
      let mustMet = 0;
      let mustTotal = 0;
      let optionalTotal = 0;
      let optionalAssessed = 0;
      let optionalSum = 0;
      let unassessable = 0;

      for (const item of criteria) {
        // ONE narrowing for both arms. `assessable` is only true where the parse found a non-null
        // fulfilment, so reading the value through the null check states that invariant to the type
        // system — where the two `?? 0` fallbacks this replaces stated it to nobody and could not be
        // reached. An item that somehow carried `assessable` without a value now counts as
        // unassessable, which is the answer the rest of this file already gives.
        const value = item.assessable ? item.fulfillment : null;
        if (value === null) unassessable += 1;
        if (item.isMust) {
          mustTotal += 1;
          // Missing information is not a failure — it sends the company to review instead of
          // striking it out. Only a known value below the requirement is a disproved Muss.
          if (value === null) mustUnknown = true;
          else if (value >= 1) mustMet += 1;
          else mustViolated = true;
        } else {
          optionalTotal += 1;
          if (value === null) continue;
          optionalAssessed += 1;
          optionalSum += value;
        }
      }

      const qualification: CompanyQualification = mustViolated
        ? "UNQUALIFIED"
        : mustUnknown
          ? "REVIEW_REQUIRED"
          : "QUALIFIED";

      // Kann only, exactly as on the person side (@adr SPARK-0030-match-result-scoring-atom-derived@1 §3): Muss is a gate, and among the
      // companies that passed it every Muss is 1.0, so folding it in would only compress the scale.
      // No Kann criteria at all → 1: there is nothing left to differentiate on, and a company that
      // met every stated requirement is not an 0 %. Kann criteria but none assessable → null:
      // 0 % would be a claim and 100 % a free pass.
      const score = optionalTotal === 0 ? 1 : optionalAssessed === 0 ? null : optionalSum / optionalAssessed;

      return {
        entryId: row.entryId,
        companyId: row.company.id,
        name: row.company.name,
        position: row.position,
        qualification,
        score,
        mustMet,
        mustTotal,
        optionalAssessed,
        optionalTotal,
        unassessable,
        criteria,
        inactive: row.company.inactive,
        industryId: row.company.industryId,
        employeeCount: row.company.employeeCount,
      } satisfies RankedCompany;
    })
    .sort((a, b) => a.position - b.position);
}

/**
 * Which band a company falls in at these thresholds.
 *
 * The ONLY threshold-dependent step, kept apart from {@link buildCompanyRanking} for that reason:
 * the arithmetic is computed once per result, the cut is recomputed whenever the reader moves a
 * slider, and neither is ever stored (genesis stores atoms only, @adr SPARK-0030-match-result-scoring-atom-derived@1 §2).
 *
 * The Muss gate outranks the score in both directions. A disproved Muss is out whatever the score —
 * including when no Kann criterion could be assessed, where "zu prüfen" would invite a second look
 * at a company that is already decided. Missing information alone is "zu prüfen" and never out.
 */
export function companyBand(row: RankedCompany, thresholds: CompanyRankThresholds): CompanyBand {
  if (row.qualification === "UNQUALIFIED") return "UNQUALIFIED";
  if (row.qualification === "REVIEW_REQUIRED" || row.score === null) return "REVIEW";
  // `c` is optional on the shared type and defaults to 0 — no floor — which is what the person
  // ranking means by leaving it out. Read it the same way rather than requiring it here.
  if (row.score < (thresholds.c ?? 0)) return "UNQUALIFIED";
  if (row.score >= thresholds.a) return "A";
  if (row.score >= thresholds.b) return "B";
  return "C";
}

/** How many companies sit in each band at these thresholds. */
export function countByBand(
  ranked: readonly RankedCompany[],
  thresholds: CompanyRankThresholds,
): Record<CompanyBand, number> {
  const counts: Record<CompanyBand, number> = { A: 0, B: 0, C: 0, REVIEW: 0, UNQUALIFIED: 0 };
  for (const row of ranked) counts[companyBand(row, thresholds)] += 1;
  return counts;
}

// ═════════════════════════════════════════════════════════════════════════════════════════════════
// THE MATCH ANALYSIS OVER A COMPANY RANKING — the person side's struct, filled from company atoms.
//
// Concept v17 (d) (the company matching model) decided that a company result
// shows what a person result shows — Match-Analyse, Top 5, Rangliste — and that the person surfaces
// are REUSED rather than rebuilt. That only works if this side hands the renderer the identical
// shape, which is why every type below is imported from `match-result-derive` and none is declared
// here: a company-shaped twin of `RankingInsights` is exactly the "two implementations of one view"
// that the spark!285 review (point 3) objected to.
//
// WHAT IS MIRRORED IS THE DECISIONS, NOT THE DATA ACCESS. `buildRankingInsights` aggregates over
// `factSkill.teamMembers[].items`; a company ranking has no team blob at all — every row IS its own
// blob (genesis `CompanyMatchResultItem`) — so the aggregation walks `RankedCompany.criteria`.
// Wherever a decision is deliberately NOT mirrored, the divergence is argued at the line that makes
// it and marked DIVERGENCE.
//
// WHAT IS DELIBERATELY ABSENT: any judgement about a company AS A WHOLE. The person side has
// `aiAssessment.suitability`, a verdict on the whole human; v17 (c) decided the company arm has none
// and shall have none, because it would be a second, unaudited number standing next to a ranking
// that is built from checked criteria. So the `ai` block below counts AI-REASONED CRITERIA and
// nothing else — on this side the model only ever judges the Fachkompetenz field (v17 (b)/(c)).
//
// AND WHAT IS ABSENT BECAUSE IT NO LONGER EXISTS: levels. Company expertise has no degree since
// v17 (the company capability model), so every company criterion is
// binary and the fulfilment values that reach this file are 0, 1 or null. Nothing below assumes
// that — a pre-v17 saved ranking still carries graded PARTIAL atoms, and they have to keep counting
// correctly — but it explains why several counters read 0 on every fresh result.

/**
 * The zero of {@link RankingPoolInsights}, and a copy of the person side's `EMPTY_POOL`.
 *
 * <p>Copied and not imported because `EMPTY_POOL` is module-private in `match-result-derive`. The
 * duplication is the price of not widening that module's surface for one consumer; the tripwire is
 * a test that pins this object literal, so a member added to the shared type fails HERE rather than
 * silently leaving the company side with a missing key.
 */
const EMPTY_COMPANY_POOL: RankingPoolInsights = {
  total: 0,
  counts: { A: 0, B: 0, C: 0, REVIEW: 0, UNQUALIFIED: 0 },
  averageScore: null,
  medianScore: null,
  topGap: null,
  qualifiedCount: 0,
};

/**
 * The answer for a ranking with no rows: the empty shape, never a throw.
 *
 * <p>Returned BY REFERENCE, as the person side returns its `EMPTY_INSIGHTS` — same identity
 * semantics, and the same inherited hazard that a consumer which mutated the arrays would poison
 * every later empty answer. Nothing in spark mutates an insights struct, and freezing here would
 * make this side throw where the person side stays silent, which is a worse kind of difference.
 */
const EMPTY_COMPANY_INSIGHTS: RankingInsights = {
  hardestMust: null,
  pool: EMPTY_COMPANY_POOL,
  topFulfilled: [],
  bottlenecks: [],
  criteria: [],
  ai: { reasonCount: 0, candidateCount: 0, criteria: [], totalItems: 0 },
  dataQuality: { missingActualCount: 0, expectedActualCount: 0, topMissing: null },
  recommendation: null,
};

/**
 * The key one criterion is recognised by ACROSS companies.
 *
 * <p>The person side prefers a stable `conditionKey` that genesis puts on `MatchResultCriterion`
 * and only falls back to serialising the condition. There is no such key here: genesis's
 * `CompanyMatchResultItem` carries the `CompanyMatchCondition` itself and nothing beside it, so the
 * serialised condition is not a fallback on this side but the only key there is. It holds because
 * both sides of the wire are Jackson-serialised from the same record per run, so the same criterion
 * yields the same property order for every company in one ranking — and a key that failed to match
 * would cost coverage of that criterion, never a wrong verdict (see the missing-atom rule below).
 */
function companyConditionKey(item: CompanyCriterionView): string {
  return item.condition === undefined ? "undefined" : JSON.stringify(item.condition);
}

/**
 * Median over a list of scores, mirroring the private `median` in `match-result-derive`: ascending
 * on a copy, the mean of the two middles for an even count, and null — never 0 — for an empty list.
 */
function poolMedian(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid]!;
  // Asserted rather than defaulted, exactly as the odd case one line above does: the list is
  // non-empty and `mid` comes from its own length, so both indices are in range. A `?? 0` here
  // stated the same invariant in a form nothing could ever reach and, worse, would have answered a
  // broken one with a plausible number instead of failing.
  return (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * The pool's own numbers: how the ranking is distributed, and how far apart its leaders are.
 *
 * <p><b>`counts`</b> comes from {@link companyBand}, so it moves with the threshold slider — the
 * only threshold-dependent member of the whole insights struct, which is why `thresholds` has to be
 * a parameter of the builder rather than a constant read here.
 *
 * <p><b>`topGap`</b>, read off the person implementation: the scores are sorted DESCENDING and it is
 * `scores[0] - scores[2]`, i.e. the drop from the best row to the THIRD best, and null below three
 * scored rows. What that measures is whether the head of the list is a plateau or a cliff — a small
 * gap says the top three are interchangeable, a large one says there is one clear leader. That is a
 * reading of the arithmetic and not of a stated intent: nothing in spark renders `topGap` today (it
 * is defined, defaulted and computed, and has no consumer), so there is no message text to check it
 * against, and no comment on the person side explains it.
 *
 * <p><b>`qualifiedCount`</b>, likewise: rows whose {@link CompanyQualification} is `QUALIFIED`, i.e.
 * rows that passed the Muss gate outright. It is NOT "rows in a band" and the two deliberately need
 * not agree — a QUALIFIED row below the C floor bands `UNQUALIFIED` but still counts here, and a
 * `REVIEW_REQUIRED` row never counts however good its Kann mean is. That is what lets a
 * threshold-independent count sit next to threshold-dependent `counts` without either being wrong.
 */
function buildCompanyPoolInsights(
  ranked: readonly RankedCompany[],
  thresholds: CompanyRankThresholds,
): RankingPoolInsights {
  // NO EMPTY GUARD HERE, deliberately: this is module-private and its one caller
  // (buildCompanyRankingInsights) has already returned for an empty list, so a second copy of the
  // same check could never run. The precondition is "at least one row" — a future second caller
  // owes it the same guard, which is why it is written down rather than left implicit.
  // DIVERGENCE — THE SCORE POPULATION IS WIDER HERE THAN ON THE PERSON SIDE.
  //
  // `buildPoolInsights` averages over the QUALIFIED rows' scores only; this averages over EVERY row
  // that has a score, whatever the Muss gate said. Two reasons, and the second is why the first is
  // allowed to win:
  //
  // 1. `total` and `counts` next to it describe ALL rows. An average drawn from a subset, printed
  //    beside band counts drawn from the whole list, describes a population the reader cannot see.
  //    On this side the number being averaged is also literally the number the bands cut on
  //    (`optionalSum / optionalAssessed`, v17 (e)), so "Ø im Pool" and the band histogram have to be
  //    the same population or they contradict each other in one box.
  // 2. The person side's qualified-only filter has NO stated reason. Its one comment there explains
  //    the OTHER filter — null scores stay out because an unassessable candidate must neither raise
  //    nor lower the mean (spark#175) — and says nothing about the qualification. So there is no
  //    decision to mirror here, only an unexplained line, and inventing a rationale for it would be
  //    worse than departing from it in the open.
  //
  // spark#175's rule itself is kept exactly: a row with no score at all stays out of both the mean
  // and the median, because 0 would be a claim nobody could make.
  //
  // `topGap` moves with this population by necessity — one struct must not mix two of them.
  const scores = ranked
    .map((row) => row.score)
    .filter((score): score is number => score !== null)
    .sort((a, b) => b - a);
  return {
    total: ranked.length,
    counts: countByBand(ranked, thresholds),
    averageScore: scores.length ? scores.reduce((sum, value) => sum + value, 0) / scores.length : null,
    medianScore: poolMedian(scores),
    topGap: scores.length >= 3 ? scores[0]! - scores[2]! : null,
    qualifiedCount: ranked.filter((row) => row.qualification === "QUALIFIED").length,
  };
}

/**
 * Which single sentence the analysis leads with — the person side's precedence, unchanged.
 *
 * <p>Order and gates are copied from `chooseRankingRecommendation`, deliberately including the two
 * middle branches the company side can reach less often: a disproved Muss first (it is the only
 * finding that removes rows from the list at all), then a data gap, then the AI share, and a
 * bottleneck last because a weak average is the mildest of the four. `null` when none of them
 * clears its gate — "nothing to change right now" is an answer, and the renderer has a message for
 * it.
 *
 * <p>The `ai` branch is NOT a whole-company verdict and must not be mistaken for the suitability
 * axis v17 (c) rejected: it says "go and read the reasons", counted over criteria the model was
 * asked about, and it says nothing about whether a company fits.
 */
function chooseCompanyRecommendation({
  hardestMustInsight,
  topMissing,
  aiReasonCount,
  aiCompanyCount,
  companyTotal,
  bottleneck,
}: {
  hardestMustInsight: RankingCriterionInsight | null;
  topMissing: RankingCriterionInsight | null;
  aiReasonCount: number;
  aiCompanyCount: number;
  companyTotal: number;
  bottleneck: RankingCriterionInsight | null;
}): RankingRecommendation | null {
  if (hardestMustInsight && hardestMustInsight.failedCount > 0) {
    return { type: "must", criterion: hardestMustInsight };
  }
  // The share is measured against `total` (= companies in the pool) and NOT against
  // `expectedActualCount`, which is the person side's arrangement: a criterion whose value is
  // missing on two of twenty companies is not worth the headline even if those two were the only
  // rows that could have carried one.
  if (topMissing && topMissing.missingActualCount / Math.max(1, topMissing.total) >= 0.4) {
    return { type: "data", criterion: topMissing };
  }
  if (aiReasonCount > 0 && aiCompanyCount / Math.max(1, companyTotal) >= 0.4) {
    return { type: "ai", reasonCount: aiReasonCount, candidateCount: aiCompanyCount };
  }
  if (bottleneck && bottleneck.averageFulfillment < 0.75) {
    return { type: "bottleneck", criterion: bottleneck };
  }
  return null;
}

/**
 * The company ranking's meta-analysis: which criteria the pool brings, which one holds it back, and
 * what the reader should do about it.
 *
 * <p>Returns the person side's {@link RankingInsights} verbatim so one component renders both
 * (v17 (d)). Threshold-dependent only through `pool.counts` — everything else is a property of the
 * stored atoms, which is why this can be recomputed on every render alongside
 * {@link buildCompanyRanking}.
 *
 * <p>Criteria are aggregated ACROSS companies by condition key, taking the FIRST row's criteria as
 * the set to report on — the person side's arrangement, and for the same reason: the rows of one
 * ranking were all evaluated against the same requirement set, so any row states it. The inherited
 * limitation is stated plainly: if the first row's blob is the one that failed to parse, the
 * criterion analysis is empty (the pool numbers are not). The alternative — a union over all rows —
 * was rejected because it would report every row that never carried a criterion as having a verdict
 * on it, which is the one thing this module refuses to do.
 */
export function buildCompanyRankingInsights(
  ranked: readonly RankedCompany[],
  thresholds: CompanyRankThresholds,
): RankingInsights {
  // Empty means empty, not a throw. Note that the company side cannot reproduce the person side's
  // odd case where the criteria source is absent while the pool is not — here the criteria and the
  // pool are read from the SAME list, so "no rows" settles both.
  //
  // Guarded on the FIRST ROW rather than on the length: it is the same condition, and it is the form
  // the type system can carry, so `reference` below reads the row's criteria directly instead of
  // through a `?? []` that no input could reach.
  const first = ranked[0];
  if (first === undefined) return EMPTY_COMPANY_INSIGHTS;
  const total = ranked.length;
  const reference = first.criteria;
  // One FIFO queue per company per condition key, so a criterion stated twice in one requirement set
  // is matched pairwise instead of both rows reading the same atom. Order-independent by
  // construction: nothing here relies on genesis emitting the atoms in the same sequence per row.
  const queuesByCompany = ranked.map((company) => {
    const queues = new Map<string, CompanyCriterionView[]>();
    for (const item of company.criteria) {
      const key = companyConditionKey(item);
      const queue = queues.get(key);
      if (queue) queue.push(item);
      else queues.set(key, [item]);
    }
    return queues;
  });

  const criterionInsights: RankingCriterionInsight[] = [];
  const aiCriteria: RankingAiCriterionInsight[] = [];
  // Distinct COMPANIES, keyed by `companyId` and not by `entryId`: the field this feeds counts
  // companies, and the row id would count a company twice if a pool ever held it twice. Mirrors the
  // person side keying on `personId` rather than on the team-member row.
  const aiCompanies = new Set<string>();
  let hardestMust: RankingInsight | null = null;
  let reasonCount = 0;
  let totalItems = 0;
  let missingActualCount = 0;
  let expectedActualCount = 0;

  for (const first of reference) {
    // The condition travels through opaque, exactly as it arrived: labelling it needs the industry
    // catalogue and `intl`, and both belong to the screen. `parseCompanyResult` has already
    // unwrapped it, so unlike the person side there is no `criterion.condition` indirection here.
    const condition = first.condition;
    // Taken from the reference row. Muss/Kann is a property of the REQUIREMENT, not of the company
    // it was evaluated against, so it cannot differ between rows of one ranking — and genesis
    // derives `isMust` from the condition itself rather than letting a caller pass it.
    const requirement = first.isMust ? "MUST" : "OPTIONAL";
    const key = companyConditionKey(first);
    let met = 0;
    let partial = 0;
    let failed = 0;
    let unknown = 0;
    let fulfillmentSum = 0;
    let aiCount = 0;
    let aiMetCount = 0;
    let missingForCriterion = 0;
    let expectedForCriterion = 0;
    const aiCompanyIds = new Set<string>();

    for (const [index, company] of ranked.entries()) {
      const item = queuesByCompany[index]?.get(key)?.shift() ?? null;
      if (item) totalItems += 1;
      const hasAiReason = Boolean(item?.aiReason?.trim());
      // THE RULE THIS FILE EXISTS FOR, applied to the aggregate: null means "nothing to judge" and
      // lands in `unknown`, never in `failed`. A known value is a verdict — 0 is disproved, not
      // absent (spark#175 / @adr SPARK-0030-match-result-scoring-atom-derived@1 §1).
      //
      // DIVERGENCE — A MISSING ATOM IS "CANNOT TELL" HERE, NOT "NOT MET".
      // `buildRankingInsights` scores an absent item as 0 and its comment says so on purpose. This
      // side sends it to `unknown` instead. An atom goes missing for exactly two reasons — a blob
      // that could not be read (`parseCompanyResult` answers with no criteria at all) or a ranking
      // saved before that requirement existed — and in both the outcome is unknown, not negative.
      // Counting it as failed would let one unreadable row print "Kriterium X schließt 3/5 Unternehmen
      // aus" with zero companies actually disproved, and this module's whole premise is that a
      // company is never told it failed a check nobody could run. The invariant the shared renderer
      // relies on is untouched: every company still lands in exactly one bucket, so
      // metCount + failedCount + unknownCount === total.
      const fulfillment = item && item.assessable ? (item.fulfillment ?? 0) : null;
      if (fulfillment == null) {
        unknown += 1;
      } else {
        fulfillmentSum += fulfillment;
        // "Met" is a known value AT OR ABOVE 1; a known value below it is failed. Binary since
        // v17 (e), so `partial` below is 0 on every fresh result — see the branch that keeps it.
        if (fulfillment >= 1) met += 1;
        else {
          failed += 1;
          // Strictly between 0 and 1: a graded miss. Only a ranking saved BEFORE v17 can produce
          // one (company expertise lost its levels, and that was the only graded arm), and those
          // results still have to open and still have to count correctly — which is why the counter
          // stays rather than being dropped as dead weight.
          if (fulfillment > 0) partial += 1;
        }
      }
      if (hasAiReason) {
        reasonCount += 1;
        aiCount += 1;
        // Only the AI-reasoned items that actually COUNT AS MET — the ones where the model settled
        // a criterion the literal 0.75-similarity check could not (v17 (b)). Reading it off the
        // same `fulfillment` the buckets used means the two can never disagree.
        if (fulfillment != null && fulfillment >= 1) aiMetCount += 1;
        aiCompanies.add(company.companyId);
        aiCompanyIds.add(company.companyId);
      }
      // DATA QUALITY, from the one signal this side has for it: `actualRecorded`.
      //
      // The person side asks a whitelist of PERSON condition typenames whether a criterion "expects
      // an actual value"; no company typename is in that list, and extending it would mean deciding
      // here which company criteria owe evidence — a product question the concept has not answered.
      // genesis already answers it per atom instead: `actual` is serialised ALWAYS, so the KEY being
      // present means this run looked for a value, and `null` inside it means it found none (the
      // company does not maintain the column, or no expertise entry matched the wording).
      //
      // An atom with NO key at all — a ranking saved before actual values were recorded — counts
      // towards NEITHER number. That is the whole reason `actualRecorded` is a separate field: an
      // old row must not be reported as a company with nothing on file.
      //
      // AI-decided items are excluded, as on the person side: the model, not a stored value, settled
      // those, so a missing value there is not a gap anyone should go and fill.
      if (item && item.actualRecorded && !hasAiReason) {
        expectedActualCount += 1;
        expectedForCriterion += 1;
        // The blank-string guard mirrors the person side. genesis promises never to write `""`
        // ("ein Leerstring liest sich wie ein Wert"), so this defends against a hand-edited or
        // third-party blob rather than against genesis.
        if (item.actual === null || item.actual.trim().length === 0) {
          missingActualCount += 1;
          missingForCriterion += 1;
        }
      }
    }

    // The MUSS criterion that excludes the most companies, first one wins a tie (strict `>` over
    // the reference order) — the person side's rule and its tie-break.
    //
    // NOTE ON WHAT THIS IS NOT: `count` is a count of FAILURES, not "met by the fewest". The two
    // come apart precisely because of unknowns — a Muss with 1 disproved and 4 unassessable rows is
    // met by nobody yet excludes one company — and the field's consumer settles it: the shared
    // renderer prints `count` as "{failed}/{total} ausgeschlossen" and draws `(total - count)/total`
    // as the share that fulfils. A met-count in that slot would claim exclusions that never
    // happened. (The donut's own label already overstates fulfilment by treating "not failed" as
    // "fulfils"; that imprecision lives on the person side's surface and is not this file's to fix.)
    if (requirement === "MUST" && failed > 0 && (hardestMust === null || failed > hardestMust.count)) {
      hardestMust = { condition, requirement, count: failed, total };
    }
    // Averaged over the ASSESSED rows only. Unknowns in the denominator would depress the mean just
    // as counting them as 0 in the numerator once did (spark#175) — and 0 for a criterion nothing
    // could assess is a sorting fallback, not a statement, which is why it only appears when there
    // is nothing at all to average.
    const assessedCount = total - unknown;
    const criterionInsight: RankingCriterionInsight = {
      condition,
      requirement,
      metCount: met,
      failedCount: failed,
      partialCount: partial,
      unknownCount: unknown,
      aiCount,
      aiMetCount,
      missingActualCount: missingForCriterion,
      expectedActualCount: expectedForCriterion,
      averageFulfillment: assessedCount > 0 ? fulfillmentSum / assessedCount : 0,
      total,
      // NO ACTUAL-VALUE DISTRIBUTION ON THIS SIDE, and this is a decision rather than an omission.
      // The person side's builder is module-private, and it decides numeric-vs-text from a whitelist
      // of PERSON typenames: no company condition is in it, so even if it were reachable a head
      // count would fall through to the text branch and an industry id would get a "most frequent
      // value" line. Declaring which company criteria are numeric is a product question v17 (d) did
      // not answer — it decided to reuse the person SURFACES, not to define company value
      // statistics — and a median printed over industry ids is worse than no axis. `null` is the
      // shape the renderer already handles: the person side returns it for suppressed conditions
      // and for single-value criteria too.
      distribution: null,
    };
    criterionInsights.push(criterionInsight);
    if (aiCount > 0) {
      aiCriteria.push({ condition, reasonCount: aiCount, candidateCount: aiCompanyIds.size });
    }
  }

  // Most-met first, the stronger average breaking a tie. `metCount > 0` keeps criteria nobody meets
  // out of a list headed "erfüllt".
  //
  // A criterion NOBODY could assess makes neither list, and that is right rather than a gap: one
  // list claims fulfilment and the other claims a proven weakness, and it supports neither. The
  // consequence is that such a criterion is absent from the analysis entirely — the person side
  // behaves the same way, and the honest place to surface it is the row's own "n nicht bewertbar",
  // which the ranking list already prints.
  const topFulfilled = criterionInsights
    .filter((c) => c.metCount > 0)
    .sort((a, b) => {
      if (a.metCount !== b.metCount) return b.metCount - a.metCount;
      return b.averageFulfillment - a.averageFulfillment;
    })
    .slice(0, 3);
  // Weakest average first, more failures breaking a tie — and only PROVEN weaknesses: filtering on
  // `metCount < total` would make every criterion with a single unassessable row a bottleneck.
  const bottlenecks = criterionInsights
    .filter((c) => c.failedCount > 0)
    .sort((a, b) => {
      if (a.averageFulfillment !== b.averageFulfillment) return a.averageFulfillment - b.averageFulfillment;
      return b.failedCount - a.failedCount;
    })
    .slice(0, 3);
  const topMissing = [...criterionInsights]
    .filter((c) => c.missingActualCount > 0)
    .sort((a, b) => b.missingActualCount - a.missingActualCount)[0] ?? null;
  const ai: RankingAiInsights = {
    reasonCount,
    // The number of COMPANIES with at least one AI-reasoned criterion. The field keeps the person
    // side's name because the shared renderer reads it; only the population behind it changes.
    candidateCount: aiCompanies.size,
    criteria: [...aiCriteria].sort((a, b) => b.reasonCount - a.reasonCount).slice(0, 3),
    // Atoms actually FOUND across the criteria × companies grid, not the size of the grid — the
    // person side's reading, and on this side the difference is exactly the missing atoms that the
    // divergence above sends to `unknown`.
    totalItems,
  };
  const dataQuality: RankingDataQualityInsights = { missingActualCount, expectedActualCount, topMissing };
  return {
    hardestMust,
    pool: buildCompanyPoolInsights(ranked, thresholds),
    topFulfilled,
    bottlenecks,
    criteria: criterionInsights,
    ai,
    dataQuality,
    recommendation: chooseCompanyRecommendation({
      // Recomputed rather than reusing `hardestMust`: the recommendation needs the full
      // `RankingCriterionInsight` (the renderer prints its failed/total), while `hardestMust` is the
      // narrower `RankingInsight`. Same criterion by construction — same filter, same key, and
      // `Array.prototype.sort` is stable, so a tie resolves to the same first-in-reference-order row.
      hardestMustInsight: criterionInsights
        .filter((c) => c.requirement === "MUST" && c.failedCount > 0)
        .sort((a, b) => b.failedCount - a.failedCount)[0] ?? null,
      topMissing,
      aiReasonCount: reasonCount,
      aiCompanyCount: aiCompanies.size,
      companyTotal: total,
      bottleneck: bottlenecks[0] ?? null,
    }),
  };
}

// ─── Beschriftung der Unternehmens-Kriterien ─────────────────────────────────
//
// Liegt hier und nicht mehr im Panel, seit der PDF-Bericht dieselben Kriterien beschriftet:
// zwei Formatierer waeren die Stelle, an der Schirm und Papier auseinanderlaufen. Dieselbe
// Aufteilung wie auf der Personen-Seite, wo labelForCondition in match-result-derive wohnt.

export interface CriterionFormatter {
  /** "Branche: IT", "Unternehmensgröße: mind. 50 Mitarbeitende", "Fachkompetenz: SQL · mind. Pioneer". */
  requirement: (condition: unknown) => string;
  /**
   * WHAT KIND OF CRITERION this is, as a group heading and a stable order.
   *
   * The company's kinds ARE its three condition types — Branche, Unternehmensgröße, Fachkompetenz —
   * and the labels are the very words {@link CriterionFormatter.requirement} already puts in front
   * of the colon, taken from the same three message ids. That is the point of living here rather
   * than in a taxonomy of its own: a heading that read "Branchen" while the line under it read
   * "Branche: Pharma" would be two names for one thing.
   *
   * The order is the requirement EDITOR's (`requirements/CompanyRequirementsPanel`), so a reader
   * who authored the criteria meets them again in the order they wrote them. A kind this build has
   * no name for keeps the honest fallback the requirement line already uses and sorts last.
   */
  kind: (condition: unknown) => { label: string; order: number };
  /**
   * The value the RUN found for that criterion.
   *
   * Takes the whole criterion and not just its condition: the value lives on the atom now, and the
   * atom also says whether the run recorded one at all — which is a different sentence from "there
   * is none" and must not be printed as it. `row` is only the fallback for a ranking stored before
   * the atoms carried values.
   */
  actual: (criterion: CompanyCriterionView, row: RankedCompany) => string;
}

/**
 * Labels for all three company criteria, plus an honest fallback for a kind a newer genesis knows
 * and this build does not.
 *
 * ALL THREE ARE NAMED NOW, INCLUDING FACHWISSEN. It used to fall through to "Weiteres Kriterium",
 * and the reason was not the name but the missing other half of the row: the panel could see the
 * company's industry and head count and nothing about its competencies, so naming the requirement
 * while printing "keine Angabe" beside it would have asserted that the company lists no such
 * expertise — which this side could not know. genesis now records the matched label and degree on
 * the atom itself (`CompanyCriterionView.actual`), captured where the fold ran, so both lines exist
 * and two expertise criteria no longer read as the same row twice.
 *
 * WHICH SOURCE THE ACTUAL COMES FROM, and why there are two. The atom is authoritative: it is the
 * state at the time of the run, so a saved ranking prints what was judged. `row`'s industryId and
 * employeeCount are consulted ONLY when the atom carried no value key at all — a ranking stored
 * before this existed — and the panel prints its note about today's values in exactly that case.
 *
 * Vocabulary is shared with the criteria editor on purpose: the Branche is named from spark's
 * industries reference data on both lines, and a degree is named with the seven-rung Fachkompetenz
 * ladder that the editor authors with and the company page displays (lib/skill-levels, @external-adr GENESIS-0021-company-competency-degree-stored-as-percent
 * §1-2). A criterion and the value it is compared against must not be written in two units.
 */
export function criterionFormatter(
  intl: IntlShape,
  industryLabelById: ReadonlyMap<string, string>,
): CriterionFormatter {
  const industryWord = intl.formatMessage({
    id: "match.result.condition.industry",
    defaultMessage: "Branche",
  });
  const sizeWord = intl.formatMessage({
    id: "match.company.employeesTitle",
    defaultMessage: "Unternehmensgröße",
  });
  const employeesUnit = intl.formatMessage({
    id: "match.company.employeesUnit",
    defaultMessage: "Mitarbeitende",
  });
  const missing = intl.formatMessage({
    id: "match.companyRanking.noValue",
    defaultMessage: "keine Angabe",
  });
  // Same word the requirement editor uses for this criterion, so the drill-down and the editor name
  // the same thing the same way.
  const expertiseWord = intl.formatMessage({
    id: "match.company.expertiseTitle",
    defaultMessage: "Fachkompetenz",
  });
  const minWord = intl.formatMessage({
    id: "match.result.condition.thresholdMin",
    defaultMessage: "mind.",
  });
  // The honest fallback for a kind a newer genesis knows and this build does not — used for the
  // requirement line AND for its group heading, so an unnamed criterion is filed under the same
  // word it is labelled with rather than under an invented category.
  const otherWord = intl.formatMessage({
    id: "match.companyRanking.otherCriterion",
    defaultMessage: "Weiteres Kriterium",
  });
  // "The company lists nothing matching this wording" — a statement about the company, and only
  // sayable because genesis recorded it. NOT "keine Angabe", which would read as an unmaintained
  // field, and never a blank, which would read as a value.
  const noMatchingExpertise = intl.formatMessage({
    id: "match.companyRanking.noMatchingExpertise",
    defaultMessage: "keine passende Fachkompetenz",
  });
  const judgedByAi = intl.formatMessage({
    id: "match.companyRanking.actualJudgedByAi",
    defaultMessage: "per KI beurteilt",
  });
  // The only honest answer for a ranking stored before the run recorded values: this side knows
  // nothing about the company's competencies at that moment, and today's list is not that moment.
  const notRecorded = intl.formatMessage({
    id: "match.companyRanking.actualNotRecorded",
    defaultMessage: "vom Lauf nicht erfasst",
  });
  const employees = (count: number) =>
    intl.formatMessage(
      { id: "match.companyPool.employeesValue", defaultMessage: "{count, number} Mitarbeitende" },
      { count },
    );
  const industryName = (id: string | null | undefined) =>
    id
      ? (industryLabelById.get(id) ??
        intl.formatMessage({
          id: "match.companyPool.unknownIndustry",
          defaultMessage: "Branche unbekannt",
        }))
      : missing;

  function typenameOf(condition: unknown): string | null {
    if (!condition || typeof condition !== "object") return null;
    const typename = (condition as { __typename?: unknown }).__typename;
    return typeof typename === "string" ? typename : null;
  }

  // A stored 0..100 degree as its rung name. A company's 0 is a real claim ("we cannot do this",
  // @external-adr GENESIS-0021-company-competency-degree-stored-as-percent §5), and the ladder has no rung for it — so it gets the company page's own word for
  // that state rather than falling back to a bare number.
  const degreeWord = (percent: number): string => {
    const level = levelFromPercent(percent);
    return level
      ? intl.formatMessage({ id: level.nameId, defaultMessage: level.name })
      : intl.formatMessage({
          id: "company.page.competencies.noCapability",
          defaultMessage: "No capability",
        });
  };

  // genesis composes an expertise actual as "<the company's label> · <degree 0..100>" and keeps the
  // degree raw, because rung names are localized and belong here. Split at the LAST separator: a
  // label may contain one, an integer cannot. Anything that does not fit that shape is printed as
  // it came, which is still better than dropping the evidence.
  const expertiseActual = (raw: string): string => {
    const cut = raw.lastIndexOf(ACTUAL_PART_SEPARATOR);
    if (cut <= 0) return raw;
    const label = raw.slice(0, cut);
    const degree = raw.slice(cut + ACTUAL_PART_SEPARATOR.length);
    if (!/^\d+$/.test(degree)) return raw;
    return `${label}${ACTUAL_PART_SEPARATOR}${degreeWord(Number(degree))}`;
  };

  return {
    requirement: (condition) => {
      const typename = typenameOf(condition);
      if (typename === "CompanyIndustryCondition") {
        const id = (condition as { industryId?: unknown }).industryId;
        return intl.formatMessage(
          { id: "match.result.condition.exact", defaultMessage: "{label}: {value}" },
          { label: industryWord, value: industryName(typeof id === "string" ? id : null) },
        );
      }
      if (typename === "CompanyEmployeeCountCondition") {
        const minimum = (condition as { minimumEmployees?: unknown }).minimumEmployees;
        return intl.formatMessage(
          { id: "match.result.condition.threshold", defaultMessage: "{label}: {word} {value}" },
          {
            label: sizeWord,
            word: intl.formatMessage({
              id: "match.result.condition.thresholdMin",
              defaultMessage: "mind.",
            }),
            value: typeof minimum === "number" ? employees(minimum) : employeesUnit,
          },
        );
      }
      if (typename === "CompanyExpertiseCondition") {
        const wording = (condition as { expertise?: unknown }).expertise;
        // STILL READ, though no new design carries it: concept v17 removed the minimum from the
        // criterion, and a design saved before that still has one. Printing it for those keeps an old
        // requirement readable as it was authored.
        const degree = (condition as { minimumDegree?: unknown }).minimumDegree;
        const named = typeof wording === "string" && wording.trim().length > 0 ? wording.trim() : null;
        // A minimum of null or <= 0 states no threshold at all — genesis' evaluator reads it that
        // way — so the clause is left off rather than printed as a rung the reader never picked.
        const withDegree =
          named != null && typeof degree === "number" && degree > 0
            ? `${named}${ACTUAL_PART_SEPARATOR}${minWord} ${degreeWord(degree)}`
            : named;
        return intl.formatMessage(
          { id: "match.result.condition.exact", defaultMessage: "{label}: {value}" },
          // A criterion whose wording is blank names nothing, and saying so is the honest reading.
          // Such a row cannot be produced by a new run any more — the ranking step refuses to run
          // while one exists — but it can already sit in a stored design.
          { label: expertiseWord, value: withDegree ?? missing },
        );
      }
      return otherWord;
    },
    kind: (condition) => {
      const typename = typenameOf(condition);
      if (typename === "CompanyIndustryCondition") return { label: industryWord, order: 0 };
      if (typename === "CompanyEmployeeCountCondition") return { label: sizeWord, order: 1 };
      if (typename === "CompanyExpertiseCondition") return { label: expertiseWord, order: 2 };
      return { label: otherWord, order: 90 };
    },
    actual: (criterion, row) => {
      const typename = typenameOf(criterion.condition);
      if (typename === "CompanyExpertiseCondition") {
        // No fallback here on purpose: an expertise actual has never existed anywhere but on the
        // atom, so an old row's is unknown rather than absent.
        if (!criterion.actualRecorded) return notRecorded;
        if (criterion.actual != null) return expertiseActual(criterion.actual);
        // A null actual with an AI reason marks the fallback judgement, not a finding of missing expertise.
        return criterion.aiReason?.trim() ? judgedByAi : noMatchingExpertise;
      }
      if (typename === "CompanyIndustryCondition") {
        if (criterion.actualRecorded) {
          return criterion.actual == null ? missing : industryName(criterion.actual);
        }
        return industryName(row.industryId);
      }
      if (typename === "CompanyEmployeeCountCondition") {
        // null is "no value", never "zero employees" — genesis allows a stored 0 and means it.
        if (criterion.actualRecorded) {
          const count = criterion.actual == null ? null : Number(criterion.actual);
          return count == null || !Number.isFinite(count) ? missing : employees(count);
        }
        return row.employeeCount == null ? missing : employees(row.employeeCount);
      }
      return missing;
    },
  };
}

/**
 * The separator genesis joins the parts of a composed actual with, and the one this panel joins a
 * requirement's parts with — one constant so the two lines of a criterion cannot drift apart.
 */
const ACTUAL_PART_SEPARATOR = " · ";
